import React, { useState, useEffect } from 'react'
import { useSearchParams, Link } from 'react-router-dom'

function AIMiniAppGenerator() {
  const [searchParams] = useSearchParams()
  const courseId = searchParams.get('courseId')
  
  const [prompt, setPrompt] = useState('')
  const [loading, setLoading] = useState(false)
  const [generatedApp, setGeneratedApp] = useState(null)
  const [drafts, setDrafts] = useState([])
  const [activeTab, setActiveTab] = useState('generate')
  const [error, setError] = useState('')

  const samplePrompts = [
    "Create an interactive math calculator for basic arithmetic operations",
    "Build a quiz app for testing knowledge of world geography",
    "Make a flashcard app for learning vocabulary words",
    "Create a simple drawing app with different colors",
    "Build a memory game with matching cards",
    "Make a typing speed test application",
    "Create a unit converter for measurements",
    "Build a simple piano keyboard with sound"
  ]

  useEffect(() => {
    fetchDrafts()
  }, [])

  const fetchDrafts = async () => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/ai/drafts', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setDrafts(data)
      }
    } catch (error) {
      console.error('Error fetching drafts:', error)
    }
  }

  const generateMiniApp = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt')
      return
    }

    setLoading(true)
    setError('')

    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/ai/mini-app', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          prompt,
          courseId: courseId || null
        })
      })

      const data = await response.json()

      if (response.ok) {
        setGeneratedApp(data)
        fetchDrafts() // Refresh drafts
      } else {
        setError(data.error || 'Failed to generate mini app')
      }
    } catch (error) {
      setError('Network error. Please try again.')
      console.error('Generation error:', error)
    } finally {
      setLoading(false)
    }
  }

  const publishApp = async (draftId) => {
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/ai/drafts/${draftId}/publish`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        fetchDrafts() // Refresh drafts
        alert('App published successfully!')
      } else {
        alert('Failed to publish app')
      }
    } catch (error) {
      console.error('Publish error:', error)
      alert('Error publishing app')
    }
  }

  const deleteDraft = async (draftId) => {
    if (!confirm('Are you sure you want to delete this draft?')) return

    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/ai/drafts/${draftId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })

      if (response.ok) {
        fetchDrafts() // Refresh drafts
      }
    } catch (error) {
      console.error('Delete error:', error)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <Link to="/" className="text-primary-600 hover:text-primary-700 font-medium">
                ← Back to Dashboard
              </Link>
              <h1 className="ml-4 text-2xl font-bold text-gray-900">AI Mini App Generator</h1>
            </div>
            {courseId && (
              <div className="text-sm text-gray-600">
                Generating for Course ID: {courseId}
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('generate')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'generate'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Generate New App
              </button>
              <button
                onClick={() => setActiveTab('drafts')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'drafts'
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                My Drafts ({drafts.length})
              </button>
            </nav>
          </div>

          {/* Generate Tab */}
          {activeTab === 'generate' && (
            <div className="space-y-6">
              <div className="bg-white shadow rounded-lg p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Generate Interactive Mini App</h2>
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="prompt" className="block text-sm font-medium text-gray-700 mb-2">
                      Describe the mini app you want to create:
                    </label>
                    <textarea
                      id="prompt"
                      rows={4}
                      className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                      placeholder="e.g., Create an interactive math calculator for basic arithmetic operations"
                      value={prompt}
                      onChange={(e) => setPrompt(e.target.value)}
                    />
                  </div>

                  {error && (
                    <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
                      {error}
                    </div>
                  )}

                  <div className="flex justify-end">
                    <button
                      onClick={generateMiniApp}
                      disabled={loading || !prompt.trim()}
                      className="inline-flex items-center px-6 py-2 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Generating...
                        </>
                      ) : (
                        'Generate Mini App'
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Sample Prompts */}
              <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Try these sample prompts:</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {samplePrompts.map((samplePrompt, index) => (
                    <button
                      key={index}
                      onClick={() => setPrompt(samplePrompt)}
                      className="text-left p-3 border border-gray-200 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <div className="text-sm text-gray-900">{samplePrompt}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Generated App Preview */}
              {generatedApp && (
                <div className="bg-white shadow rounded-lg p-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Generated App Preview</h3>
                  <div className="border border-gray-200 rounded-lg p-4 mb-4">
                    <div dangerouslySetInnerHTML={{ __html: generatedApp.html }} />
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-600">Draft ID: {generatedApp.draftId}</p>
                      <p className="text-sm text-gray-600">Preview URL: <a href={generatedApp.previewUrl} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:text-primary-700">{generatedApp.previewUrl}</a></p>
                    </div>
                    <div className="space-x-2">
                      <button
                        onClick={() => window.open(generatedApp.previewUrl, '_blank')}
                        className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        Preview
                      </button>
                      <button
                        onClick={() => publishApp(generatedApp.draftId)}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                      >
                        Publish
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Drafts Tab */}
          {activeTab === 'drafts' && (
            <div className="space-y-4">
              {drafts.length === 0 ? (
                <div className="bg-white shadow rounded-lg p-6 text-center">
                  <div className="text-gray-500 mb-4">No drafts yet</div>
                  <p className="text-sm text-gray-600 mb-4">Start by generating your first mini app!</p>
                  <button
                    onClick={() => setActiveTab('generate')}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  >
                    Generate App
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {drafts.map((draft) => (
                    <div key={draft.id} className="bg-white shadow rounded-lg p-6">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-medium text-gray-900">Mini App Draft</h3>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          draft.status === 'published' 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {draft.status}
                        </span>
                      </div>
                      
                      <div className="space-y-2 mb-4">
                        <p className="text-sm text-gray-600">
                          <strong>Prompt:</strong> {draft.prompt}
                        </p>
                        <p className="text-xs text-gray-500">
                          Created: {new Date(draft.createdAt).toLocaleDateString()}
                        </p>
                      </div>

                      <div className="space-y-2">
                        <button
                          onClick={() => window.open(draft.previewUrl, '_blank')}
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                        >
                          Preview
                        </button>
                        
                        {draft.status === 'draft' && (
                          <button
                            onClick={() => publishApp(draft.id)}
                            className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                          >
                            Publish
                          </button>
                        )}
                        
                        <button
                          onClick={() => deleteDraft(draft.id)}
                          className="w-full inline-flex justify-center items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-white hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default AIMiniAppGenerator